import React, { useCallback, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { Tooltip, Button, Menu, MenuItem } from "@mui/material";
import {
  Trash2,
  PlusCircle,
  RefreshCw,
  Eye,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../ApiFunction";
import API, { BASE_URL } from "../../Api";
import { toast } from "react-toastify";

const LoanListing = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  
  const handleNotificationMenuOpen = (event, loanId) => {
    setAnchorEl(event.currentTarget);
    setSelectedLoanId(loanId);
  };

  const handleNotificationMenuClose = () => {
    setAnchorEl(null);
    setSelectedLoanId(null);
  };

  const handleSendNotification = async (notificationType) => {
    try {
      const result = await postRequest(`${BASE_URL}/loan/send-notification`, {
        loanId: selectedLoanId,
        notificationType
      });
      
      if (result.data.success) {
        toast.success(`${notificationType} sent successfully`);
      } else {
        toast.error(result.data.message || "Failed to send notification");
      }
    } catch (err) {
      toast.error(err.message || "Error sending notification");
    }
    handleNotificationMenuClose();
  };

  
  const handleViewLoan = (loanId) => {
    navigate(`/loan/${loanId}`);
  };

  const handleTopUp = (loanId) => {
    toast.info(`Top-Up functionality for loan ${loanId} - Coming soon`);
  };

  const handleTakeover = (loanId) => {
    toast.info(`Takeover functionality for loan ${loanId} - Coming soon`);
  };

  const handleCancel = (loanId) => {
    toast.info(`Cancel functionality for loan ${loanId} - Coming soon`);
  };

  const columns = [
    { field: "id", headerName: "Loan ID", width: 120 },
    { field: "accountNo", headerName: "Account No", width: 150 },
    { field: "clientName", headerName: "Client", width: 200 },
    { field: "mobileNumber", headerName: "Mobile Number", width: 160 },
    { field: "bankAccountNumber", headerName: "Account Number", width: 180 },
    { field: "swiftCode", headerName: "SWIFT", width: 130 },
    { field: "principal", headerName: "Amount", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 220,
      valueGetter: (params) => params.row.status?.value,
      renderCell: (params) => {
        const statusColors = {
          "Submitted and pending approval": "orange",
          Active: "green",
          Closed: "gray",
          Rejected: "red",
        };
        return (
          <span
            style={{
              color: statusColors[params.value] || "black",
              fontWeight: 600,
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      field: "submittedOnDate",
      headerName: "Submitted On",
      width: 180,
      valueGetter: (params) =>
        params.row.timeline?.submittedOnDate?.join("-") || "N/A"
    },
    {
      field: "action",
      headerName: "Actions",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <div className="d-flex gap-2">
          <Tooltip title="View Loan">
            <Eye
              size={20}
              style={{ cursor: "pointer", color: "black" }}
              onClick={() => handleViewLoan(params.row.id)}
            />
          </Tooltip>
          <Tooltip title="Top-Up">
            <PlusCircle
              size={20}
              style={{ cursor: "pointer", color: "blue" }}
              onClick={() => handleTopUp(params.row.id)}
            />
          </Tooltip>
          <Tooltip title="Takeover">
            <RefreshCw
              size={20}
              style={{ cursor: "pointer", color: "purple" }}
              onClick={() => handleTakeover(params.row.id)}
            />
          </Tooltip>
          <Tooltip title="Cancel">
          {params.row.status?.value === "LOAN_CREATED" && (
            <Tooltip title="Send Notification">
              <Send
                size={20}
                style={{ cursor: "pointer", color: "green" }}
                onClick={(e) => handleNotificationMenuOpen(e, params.row.id)}
              />
            </Tooltip>
          )}
          {params.row.status?.value === "LOAN_CREATED" && (
            <Tooltip title="Send Notification">
              <Send
                size={20}
                style={{ cursor: "pointer", color: "green" }}
                onClick={(e) => handleNotificationMenuOpen(e, params.row.id)}
              />
            </Tooltip>
          )}
            <Trash2
              size={20}
              style={{ cursor: "pointer", color: "red" }}
              onClick={() => handleCancel(params.row.id)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const [rows, setRows] = useState([])

  const fetchLoans = useCallback(async (event) => {
    try {
      const result = await getRequest(`${BASE_URL}${API.ALL_EMPLOYEES_LOAN}`);
      const { success, data, message } = result.data;

      if (!success) {
        toast.error(message || "Failed to fetch loans");
        return;
      }
      const newData = data?.loans?.map((p) => ({
        id: p._id,
        accountNo: p.essApplicationNumber || "N/A",
        clientName: p.clientData?.fullName || [p.clientData?.firstName, p.clientData?.middleName, p.clientData?.lastName].filter(Boolean).join(" ") || "N/A",
        mobileNumber: p.mobileNumber || p.clientData?.mobileNumber || "N/A",
        bankAccountNumber: p.bankAccountNumber || p.clientData?.bankAccountNumber || "N/A",
        swiftCode: p.swiftCode || p.clientData?.swiftCode || "N/A",
        loanProductName: p.loanData?.productCode || "N/A",
        currency: "TZS",
        principal: p.requestedAmount || p.loanData?.requestedAmount || 0,
        approvedPrincipal: p.requestedAmount || 0,
        annualInterestRate: p.loanData?.interestRate || 0,
        repaymentFrequency: "Monthly",
        numberOfRepayments: p.tenure || p.loanData?.tenure || 0,
        tenure: p.tenure || p.loanData?.tenure || 0,
        termFrequency: p.tenure,
        termPeriodFrequencyType: { value: "Months" },
        amortization: "N/A",
        interestType: "N/A",
        status: { value: p.status || "N/A" },
        timeline: {
          submittedOnDate: p.createdAt ? [
            new Date(p.createdAt).getFullYear(),
            new Date(p.createdAt).getMonth() + 1,
            new Date(p.createdAt).getDate()
          ] : null,
          expectedMaturityDate: null
        }
      }));

      setRows(newData || [])
    } catch (err) {
      toast.error(err.message);
      console.error(err.message);
    }
  }, []);


  useEffect(() => {
    fetchLoans()
  }, [fetchLoans])

  return (
    <div className="container">
      <h5 className="mb-1">Loan Management</h5>
      <p className="text-muted mb-4">
        Review all employee loans, their current status, and available actions.
      </p>
      <Paper>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10]}
          autoHeight
          sx={{ border: 0 }}
        />
      </Paper>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleNotificationMenuClose}
      >
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_NOTIFICATION')}>
          Send Disbursement Notification
        </MenuItem>
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_FAILURE_NOTIFICATION')}>
          Send Disbursement Failure Notification
        </MenuItem>
      </Menu>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleNotificationMenuClose}
      >
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_NOTIFICATION')}>
          Send Disbursement Notification
        </MenuItem>
        <MenuItem onClick={() => handleSendNotification('LOAN_DISBURSEMENT_FAILURE_NOTIFICATION')}>
          Send Disbursement Failure Notification
        </MenuItem>
      </Menu>
    </div>
  );
};

export default LoanListing;
